// src/ConsumerApp/Screens/Home/components/OffersForYouSection.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';

// Offer Banner Assets
import welcomeOfferImg from '../images/offer.png';
import brickpointsImg from '../images/brickpoints.png';
import cementImg from '../images/cement.png';
import sentriqImg from '../images/sentriq.png';

const OFFERS = [
  {
    id: 'welcome',
    image: welcomeOfferImg,
    alt: 'Welcome Offer up to ₹1000 in savings',
    route: '/consumer/account/offers',
  },
  {
    id: 'brickpoints',
    image: brickpointsImg,
    alt: 'Brick Points exclusively for construction professionals',
    route: '/consumer/account/brick-points',
  },
  {
    id: 'cement',
    image: cementImg,
    alt: 'Up to ₹500 off on first cement order',
    route: '/consumer/search?q=cement',
  },
  {
    id: 'sentriq',
    image: sentriqImg,
    alt: 'Sentriq Concealed Metal Boxes starting at ₹29',
    route: '/consumer/search?q=sentriq',
  },
];

const AUTOPLAY_DELAY_MS = 3500;

export default function OffersForYouSection() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollTrackRef = useRef(null);

  const getStepDistance = () => {
    const el = scrollTrackRef.current;
    if (!el) return 0;
    const card = el.querySelector('.bb-offer-card');
    if (!card) return 0;
    const gap = window.innerWidth <= 768 ? 16 : 24;
    return card.offsetWidth + gap;
  };

  const handleScroll = useCallback(() => {
    const el = scrollTrackRef.current;
    if (!el) return;

    const step = getStepDistance();
    if (step <= 0) return;

    const index = Math.round(el.scrollLeft / step);
    const clamped = Math.max(0, Math.min(index, OFFERS.length - 1));

    if (clamped !== activeIndex) {
      setActiveIndex(clamped);
    }
  }, [activeIndex]);

  const scrollToSlide = (index) => {
    const el = scrollTrackRef.current;
    if (!el) return;

    const step = getStepDistance();
    const targetIdx = (index + OFFERS.length) % OFFERS.length;

    el.scrollTo({
      left: targetIdx * step,
      behavior: 'smooth',
    });
    setActiveIndex(targetIdx);
  };

  const handleNext = () => scrollToSlide(activeIndex + 1);
  const handlePrev = () => scrollToSlide(activeIndex - 1);

  useEffect(() => {
    const el = scrollTrackRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      scrollToSlide((activeIndex + 1) % OFFERS.length);
    }, AUTOPLAY_DELAY_MS);

    return () => clearInterval(timer);
  }, [activeIndex, isPaused]);

  return (
    <section
      className="bb-offers-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <style>{`
        .bb-offers-section {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
          position: relative;
          background: transparent;
        }

        .bb-offers-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .bb-offers-title {
          font-family: ${theme.typography?.headingMD?.fontFamily || 'sans-serif'};
          font-size: clamp(18px, 2vw, 22px);
          font-weight: 800;
          color: #2B2A29;
          margin: 0;
          letter-spacing: -0.2px;
        }

        .bb-offers-nav-arrows {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .bb-arrow-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid #CBD5E1;
          background-color: transparent;
          color: #2B2A29;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          outline: none;
          transition: all 0.15s ease;
        }

        .bb-arrow-btn:hover {
          border-color: #94A3B8;
          transform: translateY(-1px);
        }

        .bb-arrow-btn:active {
          transform: scale(0.95);
        }

        .bb-offers-track {
          display: flex;
          overflow-x: auto;
          gap: 24px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          padding: 4px 0 8px 0;
          width: 100%;
          box-sizing: border-box;
          background: transparent;
        }

        .bb-offers-track::-webkit-scrollbar {
          display: none;
        }

        .bb-offer-card {
          min-width: calc(50% - 12px);
          max-width: calc(50% - 12px);
          flex-shrink: 0;
          scroll-snap-align: start;
          border-radius: 18px;
          overflow: hidden;
          cursor: pointer;
          background: transparent;
          border: none;
          outline: none;
          box-shadow: none;
          transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .bb-offer-card:hover {
          transform: translateY(-3px);
        }

        .bb-offer-image {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
          user-select: none;
          background: transparent;
          border-radius: 18px;
        }

        /* STRICTLY HIDDEN ON DESKTOP AND TABLET */
        .bb-offers-dots-row {
          display: none;
        }

        @media (max-width: 1024px) {
          .bb-offers-track {
            gap: 20px;
          }
          .bb-offer-card {
            min-width: calc(65% - 10px);
            max-width: calc(65% - 10px);
            border-radius: 16px;
          }
          .bb-offer-image {
            border-radius: 16px;
          }
        }

        @media (max-width: 768px) {
          .bb-offers-nav-arrows {
            display: none;
          }

          .bb-offers-track {
            gap: 16px;
            margin-left: -12px;
            margin-right: -12px;
            padding-left: 12px;
            padding-right: 12px;
            width: calc(100% + 24px);
          }

          .bb-offer-card {
            min-width: calc(100vw - 32px);
            max-width: calc(100vw - 32px);
            border-radius: 14px;
            box-shadow: none;
            background: transparent;
          }

          .bb-offer-card:hover {
            transform: none;
          }

          .bb-offer-image {
            border-radius: 14px;
          }

          /* DISPLAY DOTS ONLY ON MOBILE VIEWPORT */
          .bb-offers-dots-row {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            margin-top: 6px;
          }

          .bb-offer-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background-color: #CBD5E1;
            cursor: pointer;
            transition: all 0.22s ease;
            border: none;
            padding: 0;
            outline: none;
          }

          .bb-offer-dot.active {
            width: 22px;
            border-radius: 999px;
            background-color: #E9001E;
          }
        }
      `}</style>

      {/* Header with Title and Desktop Arrow Buttons */}
      <div className="bb-offers-header">
        <h3 className="bb-offers-title">Offers for you</h3>
        <div className="bb-offers-nav-arrows">
          <button
            type="button"
            className="bb-arrow-btn"
            onClick={handlePrev}
            aria-label="Previous Offer"
          >
            ‹
          </button>
          <button
            type="button"
            className="bb-arrow-btn"
            onClick={handleNext}
            aria-label="Next Offer"
          >
            ›
          </button>
        </div>
      </div>

      {/* Universal Side-by-Side Track */}
      <div className="bb-offers-track" ref={scrollTrackRef}>
        {OFFERS.map((item) => (
          <div
            key={item.id}
            className="bb-offer-card"
            onClick={() => navigate(item.route)}
            title={item.alt}
          >
            <img
              src={item.image}
              alt={item.alt}
              className="bb-offer-image"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Dots rendered only on mobile via media query */}
      <div className="bb-offers-dots-row">
        {OFFERS.map((_, idx) => (
          <button
            key={idx}
            type="button"
            className={`bb-offer-dot ${idx === activeIndex ? 'active' : ''}`}
            onClick={() => scrollToSlide(idx)}
            aria-label={`Go to offer ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}